import { CdcLetterWithId, Languages } from '@livv/models';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { NextPage } from 'next';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import DisplayFullText from '@components/FullText/DisplayFullText';
import CdcLetterPdfDownloadButton from '@components/VogelActu/CdcLetterPdfDownloadButton';
import VogelActuHeader from '@components/VogelActu/VogelActuHeader';
import { MARGIN_BEFORE_FOOTER } from '@styles/consts';
import { color } from '@styles/theme';
import { cdcLetterCollection } from '@utils/collections';
import { localeMap } from '@utils/date';

const MAX_LETTERS = 6;

const protectedTextSx = {
    '& blockquote': {
        border: 0,
        color: 'primary.main',
        paddingLeft: 0,
    },
    '& h2': {
        borderBottom: '1px solid',
        borderBottomColor: `${color.primaryDefault} !important`,
        borderLeft: 0,
        borderRight: 0,
        borderTop: '1px solid',
        borderTopColor: `${color.primaryDefault} !important`,
        marginBottom: 5,
        marginTop: 8,
        paddingBottom: 2,
        paddingTop: 2,
        textAlign: 'center',
    },
    '& h2:first-of-type': {
        marginTop: 7,
    },
    '& hr': {
        backgroundColor: `${color.primaryDefault} !important`,
        border: 0,
        borderTop: '1px solid',
        borderTopColor: `${color.primaryDefault} !important`,
        color: `${color.primaryDefault} !important`,
        height: 1,
        opacity: 1,
    },
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none',
};

const capitalizeFirstCharacter = (value: string, locale: Languages): string => {
    if (!value.length) {
        return value;
    }

    return value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);
};

const normalizeLetterTitle = (title: string): string =>
    title
        .replace(/La Lettre CDC/g, 'La Lettre Livv')
        .replace(/La lettre CDC/g, 'La lettre Livv')
        .replace(/The CDC Letter/g, 'Livv Newsletter')
        .replace(/CDC Letter/g, 'Livv Newsletter');

const VogelActuCdcLetterPage: NextPage = () => {
    const { t, lang } = useTranslation('vogelActuCdcLetter');
    const [letters, setLetters] = useState<CdcLetterWithId[]>([]);
    const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [refreshAttempt, setRefreshAttempt] = useState(0);

    useEffect(() => {
        let isMounted = true;

        const loadLetters = async () => {
            setIsLoading(true);
            setHasError(false);

            try {
                const lettersQuery = query(
                    cdcLetterCollection,
                    where('isPublished', '==', true),
                    orderBy('publishedAt', 'desc'),
                    limit(MAX_LETTERS),
                );
                const snapshot = await getDocs(lettersQuery);

                if (!isMounted) {
                    return;
                }

                const documents = snapshot.docs
                    .map((docSnapshot) => docSnapshot.data())
                    .filter((letter) => letter.isPublished)
                    .slice(0, MAX_LETTERS);
                setLetters(documents);
                setSelectedLetterId(
                    (currentLetterId) => currentLetterId || documents[0]?.id || null,
                );
            } catch (error) {
                console.error(error);
                if (!isMounted) {
                    return;
                }
                setHasError(true);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadLetters().catch(() => undefined);

        return () => {
            isMounted = false;
        };
    }, [refreshAttempt]);

    const selectedLetter = useMemo(() => {
        if (!letters.length) {
            return null;
        }

        return letters.find((letter) => letter.id === selectedLetterId) || letters[0];
    }, [letters, selectedLetterId]);
    const displayedContent =
        lang === Languages.en && selectedLetter?.contentEn?.trim().length
            ? selectedLetter.contentEn
            : selectedLetter?.content;

    const previousLetters = useMemo(
        () => letters.filter((letter) => letter.id !== selectedLetter?.id),
        [letters, selectedLetter?.id],
    );
    const getLetterMonthYear = (publishedAt: CdcLetterWithId['publishedAt']) => {
        const localeLanguage = lang === Languages.en ? Languages.en : Languages.fr;

        const formattedMonthYear = format(publishedAt.toDate(), 'MMMM yyyy', {
            locale: localeMap[localeLanguage],
        });

        return capitalizeFirstCharacter(formattedMonthYear, localeLanguage);
    };
    const getLetterTitle = (letter: CdcLetterWithId): string => {
        const customTitle = lang === Languages.en ? letter.titleEn?.trim() : letter.title?.trim();

        return (
            (customTitle && normalizeLetterTitle(customTitle)) ||
            t('letterTitle', {
                monthYear: getLetterMonthYear(letter.publishedAt),
            }) ||
            ''
        );
    };
    const selectedLetterTitle = selectedLetter ? getLetterTitle(selectedLetter) : '';

    return (
        <>
            <VogelActuHeader
                subtitle={t('subtitle')}
                titleContainerSx={{ '&.MuiContainer-root': { mb: 5 } }}
            />
            <Head>
                <title>{t('pageTitle')}</title>
                <meta content={t('meta.description')} name="description" />
                <meta content="noindex" name="robots" />
            </Head>
            <Container maxWidth="md" sx={{ mb: MARGIN_BEFORE_FOOTER, mt: 4 }}>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && hasError && (
                    <Alert
                        action={
                            <Button
                                color="inherit"
                                onClick={() => setRefreshAttempt((count) => count + 1)}
                                size="small"
                            >
                                {t('retry')}
                            </Button>
                        }
                        severity="error"
                        sx={{ mb: 3 }}
                    >
                        {t('loadError')}
                    </Alert>
                )}
                {!isLoading && !hasError && !selectedLetter && (
                    <Typography
                        color="grey.300"
                        sx={{ py: 6, textAlign: 'center' }}
                        variant="body1"
                    >
                        {t('emptyState')}
                    </Typography>
                )}
                {!isLoading && !hasError && selectedLetter && (
                    <>
                        <Box sx={{ mb: 5 }}>
                            <Box
                                sx={{
                                    alignItems: 'flex-start',
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        sm: 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)',
                                        xs: '1fr',
                                    },
                                    mt: 5,
                                }}
                            >
                                <Box sx={{ display: { sm: 'block', xs: 'none' } }} />
                                <Typography
                                    color="primary"
                                    sx={{ mb: 0, minWidth: 0, mt: 0 }}
                                    textAlign="center"
                                    variant="h2"
                                >
                                    {selectedLetterTitle}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: { sm: 'flex-end', xs: 'center' },
                                        minWidth: 0,
                                    }}
                                >
                                    <CdcLetterPdfDownloadButton
                                        letterId={selectedLetter.id}
                                        letterTitle={selectedLetterTitle}
                                    />
                                </Box>
                            </Box>
                            <DisplayFullText
                                contentSx={protectedTextSx}
                                sanitizeOptions={{ insertLawLexTags: false }}
                                unsanitizedContent={displayedContent || ''}
                            />
                        </Box>

                        {previousLetters.length > 0 && (
                            <>
                                <Divider sx={{ my: 4 }} />
                                <Typography sx={{ mb: 1 }} variant="h6">
                                    {t('recentLetters', { count: previousLetters.length })}
                                </Typography>
                                <Box
                                    sx={{
                                        alignItems: 'flex-start',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {previousLetters.map((letter) => (
                                        <Button
                                            key={letter.id}
                                            onClick={() => setSelectedLetterId(letter.id)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                px: 0,
                                                textTransform: 'none',
                                            }}
                                            variant="text"
                                        >
                                            {getLetterTitle(letter)}
                                        </Button>
                                    ))}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

export default VogelActuCdcLetterPage;
